import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Video, BookOpen, Users, MessageCircle, Sparkles } from 'lucide-react';
import siteConfig from '@/config/siteConfig';

export const ProgramSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const programFeatures = [
    {
      icon: Video,
      image: "https://gillesdelieuze.com/wp-content/uploads/2025/10/elements-2-gold-22-scaled.png",
      title: "Sessions mensuelles sur Zoom",
      description: "Chaque mois, on explore ensemble un thème précis, on identifie les blocages et les opportunités, et tu repars avec des outils pratiques pour avancer."
    },
    {
      icon: BookOpen,
      image: "https://gillesdelieuze.com/wp-content/uploads/2025/10/elements-2-gold-01-scaled.png",
      title: "Ressources exclusives en ligne",
      description: "Tu auras des méditations guidées, des exercices pratiques et des enseignements à disposition pour t'aider à intégrer ce que tu apprends au quotidien."
    },
    {
      icon: Users,
      image: "https://gillesdelieuze.com/wp-content/uploads/2025/10/elements-1-gold-37-scaled.png",
      title: "Communauté bienveillante",
      description: "Tu rejoindras un groupe en ligne pour partager ton expérience, poser tes questions, et recevoir le soutien d'autres membres."
    },
    {
      icon: MessageCircle,
      image: "https://gillesdelieuze.com/wp-content/uploads/2025/10/elements-1-gold-08-scaled.png",
      title: "Accès direct à Gilles",
      description: "Que ce soit lors des sessions de groupe, par messages privés pour poser tes questions personnelles. Tu auras un accompagnement sur mesure."
    },
    {
      icon: Sparkles,
      image: "https://gillesdelieuze.com/wp-content/uploads/2025/10/elements-1-gold-20-scaled.png",
      title: "Soins quantiques",
      description: "Tu seras initié à 16 fréquences delphiques et apprendras, étape par étape, comment les utiliser pour faire des soins énergétiques puissants."
    }
  ];

  return (
    <section id="programme" ref={ref} className="section-padding bg-background relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-cosmic" />
        <div className="absolute inset-0 bg-stellar" />
      </div>

      <div className="container-luxury relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-gold-luminous font-body text-sm tracking-[0.3em] uppercase mb-4 block">
            Programme structuré
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground leading-tight mb-6">
            Un programme concret et structuré pour te guider sur ton 
            <span className="text-gradient-gold"> chemin spirituel</span>
          </h2>
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {programFeatures.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 + index * 0.1, duration: 0.6 }}
              className="group"
            >
              <div className="luxury-card rounded-3xl p-8 h-full hover:border-accent/40 transition-all duration-500 hover:-translate-y-2 flex flex-col">
                {/* Icon */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-2xl overflow-hidden bg-accent/10 flex items-center justify-center">
                    <img src={feature.image} alt="" className="w-12 h-12 object-contain" />
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                    <feature.icon className="w-5 h-5 text-accent" />
                  </div>
                </div>
                
                <h3 className="font-display text-xl text-foreground mb-4">{feature.title}</h3>
                <p className="text-muted-foreground font-body leading-relaxed flex-grow text-sm md:text-base">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <a href={siteConfig.mainCTALink} target="_blank" rel="noopener noreferrer">
            <Button className="btn-gold rounded-full px-10 py-6 text-lg">
              Je veux vivre cette transformation !
            </Button>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default ProgramSection;
